/**
 * useCommunityMediaDetails — feature hook for `CommunityMediaPage`
 * (`/community/media/:id`, where `:id` is the community message document id).
 *
 * Community counterpart of `useMediaDetails` (the chat-media hook), video-only:
 * community media playback follows the SAME encrypted-HLS pipeline chat uses
 * (task-prompt lines 1-2 / story `## Context`), so this hook delegates to
 * `InitializeCommunityVideoUseCase` (Task 13) and exposes the resolved community
 * Media CDN `videoUrl` + the decrypted `VideoDecryptionKeys` the page hands to
 * the shared `InlineVideoPlayer` (`createEncryptedLoader` → hls.js).
 *
 * Anti-deep-link gate (same deliberate behaviour as `useMediaDetails` /
 * `navigateToMediaDetails`): the community message arrives ONLY as a route arg
 * (`location.state`, set by `Navigator.navigateToCommunityMedia`). When it is
 * missing (a pasted / shared URL has no `location.state`) the hook surfaces a
 * localized args-missing error and deliberately NEVER fetches the message by id.
 *
 * Page + feature-hook split (see `presentation.md`): the page owns `useBasePage`,
 * the `screenKey` mint, `useParams`, the `location.state` snapshot, and the auth
 * gate; this hook owns state + the init side-effect and exposes the memoized
 * `onPageInit` lifecycle bridge + its own `isFirstLoad`.
 *
 * The decrypted keys stay in hook state — NEVER Redux (the known
 * Uint8Array-in-Redux serialization hazard). Only `InlineVideoPlayer`'s
 * `setInitializedVideo` (which owns the video slice) receives them, per its own
 * contract.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import type { VideoDecryptionKeys } from '@/domain/entities/video/videoDecryptionEntity';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { createInitializeCommunityVideoUseCase } from '@/domain/usecases/community/initializeCommunityVideoUseCase';
import { getAuthFirebaseUserUseCase } from '@/domain/usecases/user/getAuthFirebaseUserUseCase';
import { useLocale } from '@/presentation/contexts/LocaleContext';

/** Coarse lifecycle token mirrored onto the page root's `data-qa-status`. */
export type CommunityMediaDetailsStatus = 'loading' | 'ready' | 'error';

/**
 * Route args for `CommunityMediaPage`, carried via `location.state`. The page
 * snapshots `location.state` once and passes it here. `null` means the page was
 * opened without args (pasted / shared URL) — the anti-deep-link gate fires.
 */
export interface CommunityMediaDetailsRouteArgs {
  /** The community message to play (video). */
  message: CommunityChatMessageEntity;
}

export interface UseCommunityMediaDetailsParams {
  /** The `:id` URL param (the community message document id). Used only to
   *  confirm the route args belong to this URL — never dereferenced to fetch. */
  messageId: string | undefined;
  /** Route-args snapshot (`location.state`). `null` on the pasted-URL path. */
  routeArgs: CommunityMediaDetailsRouteArgs | null;
}

export interface UseCommunityMediaDetailsResult {
  /** Resolved community Media CDN master-playlist URL (with `?hdnts=`). `null`
   *  until ready / on error. */
  videoUrl: string | null;
  /** Decrypted AES-128 keys for the encrypted hls.js loader. `null` until ready
   *  / on error. Kept in hook state (never Redux). */
  decryptionKeys: VideoDecryptionKeys | null;
  /** Coarse status for the page root's `data-qa-status`. */
  status: CommunityMediaDetailsStatus;
  /** True until the resolution settles. Gates the page spinner. */
  isFirstLoad: boolean;
  /** Localized user-visible error message when `status === 'error'`, else null. */
  errorMessage: string | null;
  /** Lifecycle bridge — the page passes this into `useBasePage({ onInit })`. */
  onPageInit: () => void;
}

export const useCommunityMediaDetails = (
  params: UseCommunityMediaDetailsParams,
): UseCommunityMediaDetailsResult => {
  const { messageId, routeArgs } = params;
  const { l10n } = useLocale();

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [decryptionKeys, setDecryptionKeys] = useState<VideoDecryptionKeys | null>(null);
  const [status, setStatus] = useState<CommunityMediaDetailsStatus>('loading');
  const [isFirstLoad, setIsFirstLoad] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Guards against double-run of the one-shot load (React 18 StrictMode mounts
  // effects twice in dev; `useBasePage.onInit` is itself once-only, but this
  // makes the load idempotent regardless of how the page bridges it).
  const hasLoadedRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Initialize the community video: derive the current user's `uid`/`iat` the
   * SAME way `useMediaDetails.loadVideo` / `videoSlice.initializeVideo` do
   * (`firebaseUser.getIdTokenResult()`), resolve the community-wall owner from
   * the message (`messageUsersInfo.owner` — the access-gate counterparty), then
   * call `InitializeCommunityVideoUseCase` (Task 13). On success expose the
   * community Media CDN `videoUrl` + decrypted keys for `InlineVideoPlayer`. On
   * failure (no active subscription / permission denied → null callable result)
   * surface the video error rather than a stuck spinner.
   */
  const loadVideo = useCallback(
    async (args: CommunityMediaDetailsRouteArgs): Promise<void> => {
      // The video init is the only real async work in the hook
      // (`getIdTokenResult()` + the `InitializeCommunityVideoUseCase` callable),
      // so it owns the page spinner: flip `isFirstLoad` true before the await
      // work and clear it on both the success and catch paths below.
      setIsFirstLoad(true);
      try {
        const firebaseUser = getAuthFirebaseUserUseCase.execute();
        if (!firebaseUser) {
          throw new Error('No authenticated user for community video init');
        }

        // Same uid/iat derivation as `useMediaDetails` / `videoSlice.initializeVideo`.
        const idTokenResult = await firebaseUser.getIdTokenResult();
        const iat = Math.floor(
          new Date(idTokenResult.issuedAtTime).getTime() / 1000,
        );
        const uid = firebaseUser.uid;

        const result = await createInitializeCommunityVideoUseCase().execute({
          messageId: args.message.id,
          communityOwner: args.message.messageUsersInfo.owner,
          uid,
          iat,
        });

        if (
          !result.success ||
          !result.videoUrl ||
          !result.decryptionEntity
        ) {
          throw result.error ?? new Error('Failed to initialize community video');
        }

        if (!isMountedRef.current) return;

        setVideoUrl(result.videoUrl);
        setDecryptionKeys(result.decryptionEntity.keys);
        setStatus('ready');
        setErrorMessage(null);
        setIsFirstLoad(false);
      } catch (error) {
        appLogger.error(
          'Failed to initialize community video',
          'useCommunityMediaDetails',
          { error: error as Error, context: { messageId } },
        );
        if (!isMountedRef.current) return;
        setStatus('error');
        setErrorMessage(l10n.community.mediaDetails.videoError);
        setIsFirstLoad(false);
      }
    },
    [messageId, l10n.community.mediaDetails.videoError],
  );

  /**
   * One-shot load, bridged from the page's `useBasePage({ onInit })`. The
   * anti-deep-link gate runs FIRST and short-circuits before any network work,
   * so a pasted / shared URL can never trigger a by-id fetch.
   */
  const onPageInit = useCallback((): void => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    // Anti-deep-link gate: require the route arg (the message) AND require its
    // id to match the URL `messageId`. No args (or a mismatched id) → error,
    // NO by-id fetch.
    if (
      !routeArgs ||
      !routeArgs.message ||
      routeArgs.message.id !== messageId
    ) {
      setStatus('error');
      setErrorMessage(l10n.community.mediaDetails.argsMissingError);
      setIsFirstLoad(false);
      return;
    }

    void loadVideo(routeArgs);
  }, [
    routeArgs,
    messageId,
    loadVideo,
    l10n.community.mediaDetails.argsMissingError,
  ]);

  return useMemo<UseCommunityMediaDetailsResult>(
    () => ({
      videoUrl,
      decryptionKeys,
      status,
      isFirstLoad,
      errorMessage,
      onPageInit,
    }),
    [videoUrl, decryptionKeys, status, isFirstLoad, errorMessage, onPageInit],
  );
};
